// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

/**
 * App-wide light/dark theme state.
 *
 * The choice is propagated by setting `data-theme="dark"` (or omitting it)
 * on the document root.  `src/diagram/theme.css` defines all themed CSS
 * custom properties under `:root` and overrides them under
 * `[data-theme="dark"]`, and that stylesheet is imported globally from
 * `src/app/index.tsx`.  As a result, every component that consumes those
 * tokens (`var(--color-...)`) reacts to the change with no additional
 * wiring — the dashboard navbar, the editor canvas dot-grid, the static
 * diagram preview, etc.
 *
 * State lives in `document.documentElement` + `localStorage` rather than
 * React state so it survives navigation and page reloads.  Components
 * that need to mirror the current value for their own UI (e.g., the
 * navbar's "Switch to Dark mode" / "Switch to Light mode" label) read
 * `getStoredTheme()` at mount and on demand.
 */

const STORAGE_KEY = 'systems-analyzer.theme';

export type Theme = 'light' | 'dark';

/**
 * Return the persisted theme preference, falling back to the OS-level
 * `prefers-color-scheme` on first visit so the UI matches the user's
 * system before they've made an explicit choice.
 */
export function getStoredTheme(): Theme {
  if (typeof window === 'undefined') {
    return 'light';
  }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
  } catch {
    // localStorage can throw in private-browsing modes or if storage is
    // disabled; fall through to system preference.
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Apply a theme by setting the `data-theme` attribute on `<html>` and
 * persisting the choice to localStorage.  Idempotent.
 */
export function applyTheme(theme: Theme): void {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', theme);
  }
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // ignore storage errors
    }
  }
}

/**
 * Flip light <-> dark, persist the new value, and return it so callers
 * can mirror the change in their own derived state on the same tick.
 */
export function toggleTheme(): Theme {
  const next: Theme = getStoredTheme() === 'dark' ? 'light' : 'dark';
  applyTheme(next);
  return next;
}
